/**
 * Alarm threshold evaluation and escalation logic
 */

const ALARM_LEVELS = {
  ADVISORY: 'advisory',
  WARNING: 'warning',
  CRISIS: 'crisis',
};

const ALARM_LEVEL_PRIORITY = {
  crisis: 3,
  warning: 2,
  advisory: 1,
};

/**
 * Evaluate all vitals and return active alarms
 * Called every 500ms or on vital change
 * @param {object} vitals
 * @param {{ skipKeys?: string[] }} options — keys to exclude (e.g. NIBP during cycling)
 */
export function evaluateAlarms(vitals, { skipKeys = [] } = {}) {
  const activeAlarms = [];

  Object.entries(vitals).forEach(([vitalName, vital]) => {
    if (skipKeys.includes(vitalName)) return;
    const { current, alarmLow, alarmHigh } = vital;

    // Determine alarm level based on thresholds
    let alarmLevel = null;

    // Crisis: beyond 10% of alarm range
    if (current < alarmLow * 0.9) {
      alarmLevel = ALARM_LEVELS.CRISIS;
    } else if (current > alarmHigh * 1.1) {
      alarmLevel = ALARM_LEVELS.CRISIS;
    }
    // Warning: beyond alarm threshold
    else if (current < alarmLow) {
      alarmLevel = ALARM_LEVELS.WARNING;
    } else if (current > alarmHigh) {
      alarmLevel = ALARM_LEVELS.WARNING;
    }
    // Advisory: within 5% of the alarm range from either threshold
    else {
      const range = alarmHigh - alarmLow;
      const band = range * 0.05;
      if (current < alarmLow + band) {
        alarmLevel = ALARM_LEVELS.ADVISORY;
      } else if (current > alarmHigh - band) {
        alarmLevel = ALARM_LEVELS.ADVISORY;
      }
    }

    if (alarmLevel) {
      activeAlarms.push({
        vitalName,
        level: alarmLevel,
        value: Math.round(current * 100) / 100,
        unit: vital.unit,
      });
    }
  });

  // Sort by priority (crisis first)
  activeAlarms.sort((a, b) => ALARM_LEVEL_PRIORITY[b.level] - ALARM_LEVEL_PRIORITY[a.level]);

  return activeAlarms;
}

// ═══════════════════════════════════════════════════════════════════════════
// IEC 60601-1-8 compliant alarm tones
//
// Each pulse uses additive synthesis (5 harmonics) for the required ≥4
// harmonic peaks between 300–4000 Hz, all within 15 dB of each other.
//
// Urgency patterns:
//   High (crisis):   10-pulse burst — two groups of 5, ~75 ms pulses
//   Medium (warning): 3-pulse burst — rising pitch (A4 → C5 → E5)
//   Low (advisory):   2-pulse burst — falling pitch (E5 → C5)
//
// Envelope: 20 ms linear rise / 20 ms linear fall per pulse (no pops).
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create a single harmonically rich alarm pulse.
 *
 * @param {AudioContext} ctx
 * @param {number} freq     — fundamental frequency (Hz)
 * @param {number} dur      — pulse duration (seconds)
 * @param {number} start    — audioContext.currentTime offset
 * @param {number} vol      — master volume 0–1 (default 0.12)
 */
function alarmPulse(ctx, freq, dur, start, vol = 0.12) {
  const master = ctx.createGain();
  master.connect(ctx.destination);

  // Envelope: 20 ms rise, sustain, 20 ms fall (IEC 60601-1-8 §7.5–100 ms)
  const rise = 0.02;
  const fall = 0.02;
  master.gain.setValueAtTime(0, start);
  master.gain.linearRampToValueAtTime(vol, start + rise);
  master.gain.setValueAtTime(vol, start + dur - fall);
  master.gain.linearRampToValueAtTime(0.0001, start + dur);

  // Additive synthesis: fundamental + 4 harmonics
  // Amplitude ratios ≈ [0, -1.9, -4.4, -6.9, -10.5] dB → within 15 dB ✓
  const harmonicGains = [1.0, 0.8, 0.6, 0.45, 0.3];

  for (let h = 0; h < harmonicGains.length; h++) {
    const hFreq = freq * (h + 1);
    if (hFreq > 4000) break; // stay within IEC frequency ceiling

    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = hFreq;
    g.gain.value = harmonicGains[h];
    osc.connect(g);
    g.connect(master);
    osc.start(start);
    osc.stop(start + dur + 0.01); // tiny margin to prevent clicks
  }
}

/**
 * Play IEC 60601-1-8 alarm tone for a given priority level.
 * Returns the total duration (seconds) of the burst so callers can schedule
 * the next repeat at the appropriate interval.
 */
export function playAlarmTone(audioContext, alarmLevel) {
  if (!audioContext) return 0;

  try {
    const now = audioContext.currentTime;

    switch (alarmLevel) {
      case ALARM_LEVELS.CRISIS: {
        // High priority: 10-pulse burst (two identical groups of 5)
        // IEC 60601-1-8: pulse 25–75 ms; we use 75 ms for audibility
        const pulse = 0.075;
        const gap = 0.075;
        const groupGap = 0.25;
        const freq = 523; // C5

        let t = now;
        for (let g = 0; g < 2; g++) {
          for (let i = 0; i < 5; i++) {
            alarmPulse(audioContext, freq, pulse, t);
            t += pulse + gap;
          }
          t += groupGap - gap; // replace last inter-pulse gap with group gap
        }
        return t - now;
      }

      case ALARM_LEVELS.WARNING: {
        // Medium priority: 3-pulse burst, rising pitch
        const pulse = 0.15;
        const gap = 0.1;
        const freqs = [440, 523, 659]; // A4 → C5 → E5

        let t = now;
        for (let i = 0; i < freqs.length; i++) {
          alarmPulse(audioContext, freqs[i], pulse, t);
          t += pulse + gap;
        }
        return t - now;
      }

      case ALARM_LEVELS.ADVISORY: {
        // Low priority: 2-pulse burst, falling pitch
        // IEC 60601-1-8: pulse 400–600 ms for low priority
        const pulse = 0.4;
        const gap = 0.15;
        const freqs = [659, 523]; // E5 → C5

        let t = now;
        for (let i = 0; i < freqs.length; i++) {
          alarmPulse(audioContext, freqs[i], pulse, t);
          t += pulse + gap;
        }
        return t - now;
      }

      default:
        return 0;
    }
  } catch (e) {
    console.error('Failed to play alarm tone:', e);
    return 0;
  }
}

/**
 * Play a single beep (used by SpO₂ pulse tone — kept simple/clean)
 */
function playBeep(audioContext, frequency, duration, startTime) {
  const osc = audioContext.createOscillator();
  const gain = audioContext.createGain();

  osc.connect(gain);
  gain.connect(audioContext.destination);

  osc.frequency.value = frequency;
  osc.type = 'sine';

  // Gentle envelope to avoid clicks
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(0.25, startTime + 0.01);
  gain.gain.setValueAtTime(0.25, startTime + duration - 0.02);
  gain.gain.linearRampToValueAtTime(0.0001, startTime + duration);

  osc.start(startTime);
  osc.stop(startTime + duration + 0.01);
}

/**
 * SpO₂ pulse tone — pitch-modulated by saturation value
 *
 * Clinical standard (GE Carescape / most pulse oximeters):
 *   100% → 675 Hz (highest pitch)
 *   80%  → 175 Hz (lowest pitch, immediate hypoxemia warning)
 *   Discrete 25 Hz steps per 1% SpO₂ change
 *   Below 80%: clamped at 175 Hz
 *   Above 100%: clamped at 675 Hz
 *
 * The tone is a short sine beep (100 ms) fired once per R-wave.
 */
export function playSpO2Beep(audioContext, spo2Value) {
  if (!audioContext) return;

  try {
    const now = audioContext.currentTime;
    // Round to integer — discrete 1% steps
    const spo2 = Math.round(spo2Value);
    // Linear map: 80% → 175 Hz, 100% → 675 Hz (25 Hz per 1%)
    const frequency = Math.max(175, Math.min(675, 175 + (spo2 - 80) * 25));
    playBeep(audioContext, frequency, 0.1, now);
  } catch (e) {
    console.error('Failed to play SpO2 beep:', e);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// NIBP cuff cycling sound
//
// Three phases that mimic an automatic oscillometric NIBP device:
//   Phase 1 — Electric pump inflation (0–6 s): band-pass filtered noise
//             at ~300 Hz (motor fundamental) + 800 Hz harmonic layer.
//   Phase 2 — Oscillometric detection (6–18 s): near-silent; cuff holds
//             pressure while the algorithm detects pulse oscillations.
//   Phase 3 — Valve deflation (18–21 s): low-pass noise burst that decays
//             exponentially — the "whoosh" of air releasing.
// ═══════════════════════════════════════════════════════════════════════════

/** Create a white-noise AudioBufferSourceNode of the requested duration. */
function makeNoiseSource(ctx, durationSec) {
  const sr = ctx.sampleRate;
  const len = Math.ceil(sr * durationSec);
  const buf = ctx.createBuffer(1, len, sr);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  return src;
}

/**
 * Play BP cuff cycling sound.
 * Returns the total sound duration in seconds (~21 s).
 */
export function playNibpCuffCycle(audioContext) {
  if (!audioContext) return 21;

  try {
    const t = audioContext.currentTime;
    const INFLATE = 6;
    const DETECT = 12;
    const DEFLATE = 3;

    // ── Phase 1a: pump fundamental (~300 Hz band-pass noise) ──────────────
    const pump1 = makeNoiseSource(audioContext, INFLATE);
    const bpf1 = audioContext.createBiquadFilter();
    bpf1.type = 'bandpass';
    bpf1.frequency.value = 300;
    bpf1.Q.value = 2;
    const g1 = audioContext.createGain();
    g1.gain.setValueAtTime(0, t);
    g1.gain.linearRampToValueAtTime(0.09, t + 0.4);
    g1.gain.linearRampToValueAtTime(0.11, t + INFLATE - 0.8);
    g1.gain.linearRampToValueAtTime(0, t + INFLATE);
    pump1.connect(bpf1); bpf1.connect(g1); g1.connect(audioContext.destination);
    pump1.start(t); pump1.stop(t + INFLATE);

    // ── Phase 1b: harmonic layer (~800 Hz) adds mechanical texture ─────────
    const pump2 = makeNoiseSource(audioContext, INFLATE);
    const bpf2 = audioContext.createBiquadFilter();
    bpf2.type = 'bandpass';
    bpf2.frequency.value = 800;
    bpf2.Q.value = 3.5;
    const g2 = audioContext.createGain();
    g2.gain.setValueAtTime(0, t);
    g2.gain.linearRampToValueAtTime(0.03, t + 0.4);
    g2.gain.linearRampToValueAtTime(0, t + INFLATE);
    pump2.connect(bpf2); bpf2.connect(g2); g2.connect(audioContext.destination);
    pump2.start(t); pump2.stop(t + INFLATE);

    // ── Phase 3: valve release (deflation whoosh) ──────────────────────────
    const deflateT = t + INFLATE + DETECT;
    const hiss = makeNoiseSource(audioContext, DEFLATE);
    const lpf = audioContext.createBiquadFilter();
    lpf.type = 'lowpass';
    lpf.frequency.value = 1400;
    const g3 = audioContext.createGain();
    g3.gain.setValueAtTime(0.20, deflateT);
    g3.gain.exponentialRampToValueAtTime(0.001, deflateT + DEFLATE);
    hiss.connect(lpf); lpf.connect(g3); g3.connect(audioContext.destination);
    hiss.start(deflateT); hiss.stop(deflateT + DEFLATE);

  } catch (e) {
    console.error('Failed to play NIBP cuff sound:', e);
  }

  return 21; // total cycling duration in seconds
}

// ═══════════════════════════════════════════════════════════════════════════
// Defibrillator / Cardioverter sound sequence
//
// Sequence:
//   Announcement — Web Speech API speaks "Charging pads" in a male voice.
//   Phase 1 — Capacitor charging (~2–3 s): sawtooth oscillator sweeps
//             from ~80 Hz up to ~900–1200 Hz (rising whine) + bandpass noise.
//   Phase 2 — "Charged" alert (2-tone beep): 880 Hz + 1175 Hz pulses.
//   Phase 3 — Shock delivery: low-frequency thump + broadband crackle.
//
// `onShock` fires at the shock moment so the caller can apply vitals changes.
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// Needle decompression — pressurised air release "hiss"
//
// When the needle enters the pleural space of a tension pneumothorax, trapped
// air under pressure escapes — a sharp onset followed by a sustained, decaying
// hiss. Modelled as high-passed white noise with a fast attack and a long
// exponential decay (~2.5 s), plus a touch of band-passed noise for the
// turbulent "whistle" character.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Play the air-release hiss of a needle decompression.
 * @param {AudioContext} ctx
 * @returns {number} total duration in seconds
 */
export function playPneumothoraxRelease(ctx) {
  if (!ctx) return 0;

  try {
    const t = ctx.currentTime;
    const dur = 2.6;

    // ── Main hiss: high-passed noise, sharp attack, long exponential decay ──
    const hiss = makeNoiseSource(ctx, dur);
    const hpf = ctx.createBiquadFilter();
    hpf.type = 'highpass';
    hpf.frequency.value = 1800;
    // Sweep the cutoff downward as pressure falls (brighter → duller)
    hpf.frequency.setValueAtTime(2600, t);
    hpf.frequency.exponentialRampToValueAtTime(900, t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(0.32, t + 0.04);   // sharp onset (~40 ms)
    g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
    hiss.connect(hpf); hpf.connect(g); g.connect(ctx.destination);
    hiss.start(t); hiss.stop(t + dur);

    // ── Turbulent whistle: narrow band-pass adds the high "psss" character ──
    const whistle = makeNoiseSource(ctx, dur * 0.7);
    const bpf = ctx.createBiquadFilter();
    bpf.type = 'bandpass';
    bpf.frequency.value = 3200;
    bpf.Q.value = 4;
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0.0001, t);
    g2.gain.linearRampToValueAtTime(0.10, t + 0.05);
    g2.gain.exponentialRampToValueAtTime(0.0006, t + dur * 0.7);
    whistle.connect(bpf); bpf.connect(g2); g2.connect(ctx.destination);
    whistle.start(t); whistle.stop(t + dur * 0.7);

    return dur;
  } catch (e) {
    console.error('Failed to play pneumothorax release sound:', e);
    return 0;
  }
}

/**
 * Play a bundled audio file and call `onDone` when it finishes (or on error).
 * Uses HTMLAudioElement — works in all browsers, no AudioContext needed.
 */
function playClip(src, onDone = () => {}) {
  try {
    const audio = new Audio(src);
    audio.onended = () => onDone();
    audio.onerror = () => onDone();
    audio.play().catch(() => onDone());
  } catch {
    onDone();
  }
}

/** Public: fire-and-forget playback of a bundled audio clip (e.g. CPR voice). */
export function playAudioClip(src) {
  playClip(src);
}

// Fixed time slot for the "Charging pads" announcement before the whine starts.
// Using a fixed lead (rather than waiting for the clip's onended) keeps the whole
// sequence deterministic, so the instructor can time the vitals change to the
// shock moment while the MONITOR plays the sound.
const SHOCK_VOICE_LEAD = 1.6; // seconds

/** Time from the start of the audio sequence to the shock discharge (seconds). */
function shockAudioSpan(type) {
  const chargeTime = type === 'defibrillation' ? 5.0 : 3.8;
  return chargeTime + 0.05 + 2 * 0.16 + 2.0; // charge + beep + 2 s pause
}

/** Total delay (ms) from triggering the sequence to the shock moment. */
export function shockDelayMs(type) {
  return Math.round((SHOCK_VOICE_LEAD + shockAudioSpan(type)) * 1000);
}

/**
 * Play a full defibrillator charge→charged→shock sequence (sound only).
 * Announces "Charging pads", plays the capacitor whine, the charged beep, a
 * 2 s pause, then "Delivering shock" simultaneous with the discharge.
 *
 * @param {AudioContext} ctx
 * @param {'cardioversion'|'defibrillation'} type
 */
export function playShockSequence(ctx, type) {
  if (!ctx) return;
  playClip('/audio/charging_pads.mp3');
  setTimeout(() => _playShockAudio(ctx, type), SHOCK_VOICE_LEAD * 1000);
}

/** Internal: plays the charge→charged→shock audio. */
function _playShockAudio(ctx, type) {
  try {
    const isDefib = type === 'defibrillation';
    const chargeTime = isDefib ? 5.0 : 3.8;
    const shockDur = isDefib ? 0.7 : 0.5;
    const t = ctx.currentTime;

    // ── Phase 1: rising capacitor whine ─────────────────────────────────
    const chargeOsc = ctx.createOscillator();
    const chargeGain = ctx.createGain();
    chargeOsc.type = 'sawtooth';
    chargeOsc.frequency.setValueAtTime(80, t);
    chargeOsc.frequency.exponentialRampToValueAtTime(
      isDefib ? 2800 : 2200,
      t + chargeTime - 0.05
    );
    chargeGain.gain.setValueAtTime(0, t);
    chargeGain.gain.linearRampToValueAtTime(0.07, t + 0.1);
    chargeGain.gain.setValueAtTime(0.07, t + chargeTime - 0.08);
    chargeGain.gain.linearRampToValueAtTime(0, t + chargeTime);
    chargeOsc.connect(chargeGain);
    chargeGain.connect(ctx.destination);
    chargeOsc.start(t);
    chargeOsc.stop(t + chargeTime + 0.05);

    // Bandpass noise adds mechanical texture to the charge sound
    const cNoise = makeNoiseSource(ctx, chargeTime);
    const cBpf = ctx.createBiquadFilter();
    cBpf.type = 'bandpass';
    cBpf.frequency.value = 400;
    cBpf.Q.value = 1.5;
    const cNoiseGain = ctx.createGain();
    cNoiseGain.gain.value = 0.02;
    cNoise.connect(cBpf);
    cBpf.connect(cNoiseGain);
    cNoiseGain.connect(ctx.destination);
    cNoise.start(t);
    cNoise.stop(t + chargeTime);

    // ── Phase 2: "charged" two-tone alert beep ───────────────────────────
    const beepT = t + chargeTime + 0.05;
    [880, 1175].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const s = beepT + i * 0.16;
      g.gain.setValueAtTime(0, s);
      g.gain.linearRampToValueAtTime(0.18, s + 0.015);
      g.gain.setValueAtTime(0.18, s + 0.1);
      g.gain.linearRampToValueAtTime(0, s + 0.13);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(s);
      osc.stop(s + 0.16);
    });

    // ── Phase 3: shock discharge (2 s pause after charged beep) ─────────
    const shockT = beepT + 2 * 0.16 + 2.0;

    // Low-frequency thump (body impedance current)
    const shockOsc = ctx.createOscillator();
    const shockGain = ctx.createGain();
    shockOsc.type = 'sine';
    shockOsc.frequency.setValueAtTime(isDefib ? 55 : 80, shockT);
    shockOsc.frequency.exponentialRampToValueAtTime(20, shockT + shockDur);
    shockGain.gain.setValueAtTime(0, shockT);
    shockGain.gain.linearRampToValueAtTime(isDefib ? 0.45 : 0.3, shockT + 0.005);
    shockGain.gain.exponentialRampToValueAtTime(0.001, shockT + shockDur);
    shockOsc.connect(shockGain);
    shockGain.connect(ctx.destination);
    shockOsc.start(shockT);
    shockOsc.stop(shockT + shockDur + 0.05);

    // Broadband crackle (electrical arc)
    const sNoise = makeNoiseSource(ctx, shockDur);
    const sLpf = ctx.createBiquadFilter();
    sLpf.type = 'lowpass';
    sLpf.frequency.value = isDefib ? 3000 : 2000;
    const sNoiseGain = ctx.createGain();
    sNoiseGain.gain.setValueAtTime(isDefib ? 0.55 : 0.38, shockT);
    sNoiseGain.gain.exponentialRampToValueAtTime(0.001, shockT + shockDur * 0.4);
    sNoise.connect(sLpf);
    sLpf.connect(sNoiseGain);
    sNoiseGain.connect(ctx.destination);
    sNoise.start(shockT);
    sNoise.stop(shockT + shockDur);

    // At the shock moment: announce "Delivering shock"
    const delayMs = Math.max(0, (shockT - ctx.currentTime) * 1000);
    setTimeout(() => playClip('/audio/delivering_shock.mp3'), delayMs);

    return shockT - t + shockDur;
  } catch (e) {
    console.error('Failed to play shock sequence:', e);
    return 0;
  }
}

/**
 * Create or resume audio context (required for Web Audio API)
 */
export function initializeAudioContext() {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();

  if (audioContext.state === 'suspended') {
    // Resume on user gesture
    document.addEventListener(
      'click',
      () => {
        if (audioContext.state === 'suspended') {
          audioContext.resume();
        }
      },
      { once: true }
    );
  }

  return audioContext;
}
